import React, { useState, useEffect, useContext } from "react";
import {
  Paper,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  People,
  TrendingUp,
  Star,
  EmojiEvents,
  Visibility,
  Phone,
  Email,
} from "@mui/icons-material";
import { AuthContext } from "../../context/Auth/AuthContext";
import { toast } from "react-toastify";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

const TeamDashboard = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [teamData, setTeamData] = useState({
    totalMembers: 0,
    activeMembers: 0,
    topPerformer: "-",
    teamGoal: 0,
    currentRevenue: 0,
    members: [],
  });

  useEffect(() => {
    fetchTeamData();
  }, []);

  const numberLocale = (() => {
    const l = String(i18n.language || "").toLowerCase();
    if (l.startsWith("pt")) return "pt-BR";
    if (l.startsWith("en")) return "en-US";
    if (l.startsWith("fr")) return "fr-FR";
    if (l.startsWith("it")) return "it-IT";
    return "es-ES";
  })();

  const formatMoney = (value) => {
    const n = Number(value || 0);
    if (!Number.isFinite(n)) return `$${(0).toLocaleString(numberLocale)}`;
    return `$${n.toLocaleString(numberLocale)}`;
  };

  const fetchTeamData = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/team/dashboard");
      if (data) {
        setTeamData({
          totalMembers: data.totalMembers || 0,
          activeMembers: data.activeMembers || 0,
          topPerformer: data.topPerformer || "-",
          teamGoal: data.teamGoal || 0,
          currentRevenue: data.currentRevenue || 0,
          members: data.members || [],
        });
      }
    } catch (error) {
      toast.error(i18n.t("teamDashboard.errors.load"));
    } finally {
      setLoading(false);
    }
  };

  const teamGoalProgress = teamData.teamGoal > 0
    ? (teamData.currentRevenue / teamData.teamGoal) * 100
    : 0;

  const MetricCard = ({ title, value, icon: Icon, color, subtitle }) => (
    <Card
      sx={{
        height: "100%",
        background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
        borderLeft: `4px solid ${color}`,
      }}
    >
      <CardContent>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Typography color="textSecondary" variant="body2" fontWeight={600}>
            {title}
          </Typography>
          <Icon sx={{ color, fontSize: 28 }} />
        </Box>
        <Typography variant="h4" fontWeight={700} sx={{ mb: 1, color }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="textSecondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const getInitials = (name) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  const getPerformanceColor = (performance) => {
    if (performance >= 100) return "#10b981";
    if (performance >= 80) return "#f59e0b";
    return "#ef4444";
  };

  return (
    <Paper
      sx={{
        p: 3,
        minHeight: "calc(100vh - 100px)",
        background: "linear-gradient(to bottom, #f8f9fa 0%, #ffffff 100%)",
      }}
      elevation={0}
    >
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          {i18n.t("teamDashboard.title")}
        </Typography>
        <Typography variant="body1" color="textSecondary">
          {i18n.t("teamDashboard.subtitle")}
        </Typography>
      </Box>

      {loading ? (
        <LinearProgress sx={{ mb: 3 }} />
      ) : (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title={i18n.t("teamDashboard.metrics.teamMembers")}
                value={teamData.totalMembers}
                icon={People}
                color="#6366f1"
                subtitle={i18n.t("teamDashboard.metrics.activeMembersSubtitle", {
                  count: teamData.activeMembers,
                })}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title={i18n.t("teamDashboard.metrics.teamRevenue")}
                value={formatMoney(teamData.currentRevenue)}
                icon={TrendingUp}
                color="#10b981"
                subtitle={i18n.t("teamDashboard.metrics.thisMonth")}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title={i18n.t("teamDashboard.metrics.topSeller")}
                value={teamData.topPerformer.split(" ")[0]}
                icon={Star}
                color="#f59e0b"
                subtitle={i18n.t("teamDashboard.metrics.topSellerSubtitle")}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title={i18n.t("teamDashboard.metrics.teamGoal")}
                value={`${teamGoalProgress.toFixed(0)}%`}
                icon={EmojiEvents}
                color="#ec4899"
                subtitle={formatMoney(teamData.teamGoal)}
              />
            </Grid>
          </Grid>

          {/* Team Goal Progress */}
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {i18n.t("teamDashboard.goal.title")}
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" color="textSecondary">
                    {i18n.t("teamDashboard.goal.completed", {
                      percent: teamGoalProgress.toFixed(1),
                    })}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatMoney(teamData.currentRevenue)} / {formatMoney(teamData.teamGoal)}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(teamGoalProgress, 100)}
                  sx={{
                    height: 12,
                    borderRadius: 6,
                    backgroundColor: "#e5e7eb",
                    "& .MuiLinearProgress-bar": {
                      background:
                        "linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%)",
                      borderRadius: 6,
                    },
                  }}
                />
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  {teamGoalProgress >= 100
                    ? i18n.t("teamDashboard.goal.reached")
                    : i18n.t("teamDashboard.goal.remaining", {
                        amount: formatMoney(teamData.teamGoal - teamData.currentRevenue),
                      })}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Team Members Table */}
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {i18n.t("teamDashboard.table.title")}
              </Typography>
              <TableContainer sx={{ mt: 2 }}>
                <Table>
                  <TableHead sx={{ backgroundColor: "#f8f9fa" }}>
                    <TableRow>
                      <TableCell>
                        <strong>{i18n.t("teamDashboard.table.headers.seller")}</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>{i18n.t("teamDashboard.table.headers.leads")}</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>{i18n.t("teamDashboard.table.headers.conversions")}</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{i18n.t("teamDashboard.table.headers.revenue")}</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{i18n.t("teamDashboard.table.headers.goal")}</strong>
                      </TableCell>
                      <TableCell>
                        <strong>{i18n.t("teamDashboard.table.headers.performance")}</strong>
                      </TableCell>
                      <TableCell>
                        <strong>{i18n.t("teamDashboard.table.headers.status")}</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{i18n.t("teamDashboard.table.headers.actions")}</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {teamData.members.map((member) => (
                      <TableRow key={member.id} hover>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Avatar
                              sx={{
                                mr: 2,
                                bgcolor: "#6366f1",
                                width: 40,
                                height: 40,
                              }}
                            >
                              {getInitials(member.name)}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {member.name}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {member.role}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={member.leads}
                            size="small"
                            sx={{ backgroundColor: "#dbeafe", color: "#1e40af" }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={member.conversions}
                            size="small"
                            sx={{ backgroundColor: "#d1fae5", color: "#065f46" }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600}>
                            {formatMoney(member.revenue)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="textSecondary">
                            {formatMoney(member.target)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 0.5,
                              }}
                            >
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                sx={{
                                  color: getPerformanceColor(member.performance),
                                }}
                              >
                                {member.performance.toFixed(1)}%
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(member.performance, 100)}
                              sx={{
                                height: 6,
                                borderRadius: 3,
                                backgroundColor: "#e5e7eb",
                                "& .MuiLinearProgress-bar": {
                                  backgroundColor: getPerformanceColor(
                                    member.performance
                                  ),
                                  borderRadius: 3,
                                },
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={
                              member.status === "active"
                                ? i18n.t("teamDashboard.status.active")
                                : i18n.t("teamDashboard.status.inactive")
                            }
                            size="small"
                            sx={{
                              backgroundColor:
                                member.status === "active"
                                  ? "#10b981"
                                  : "#6b7280",
                              color: "white",
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={i18n.t("teamDashboard.tooltips.viewDetails")}>
                            <IconButton size="small" color="primary">
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={i18n.t("teamDashboard.tooltips.call")}>
                            <IconButton
                              size="small"
                              color="primary"
                              href={`tel:${member.phone}`}
                            >
                              <Phone fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={i18n.t("teamDashboard.tooltips.email")}>
                            <IconButton
                              size="small"
                              color="primary"
                              href={`mailto:${member.email}`}
                            >
                              <Email fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Summary */}
              <Box sx={{ mt: 3, p: 2, backgroundColor: "#f8f9fa", borderRadius: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="textSecondary">
                      {i18n.t("teamDashboard.summary.totalLeads")}:
                    </Typography>
                    <Typography variant="h6" fontWeight={700} color="primary">
                      {teamData.members.reduce((sum, m) => sum + m.leads, 0)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="textSecondary">
                      {i18n.t("teamDashboard.summary.totalConversions")}:
                    </Typography>
                    <Typography variant="h6" fontWeight={700} color="#10b981">
                      {teamData.members.reduce((sum, m) => sum + m.conversions, 0)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="textSecondary">
                      {i18n.t("teamDashboard.summary.conversionRate")}:
                    </Typography>
                    <Typography variant="h6" fontWeight={700} color="#f59e0b">
                      {(() => {
                        const totalLeads = teamData.members.reduce((sum, m) => sum + m.leads, 0);
                        const totalConversions = teamData.members.reduce((sum, m) => sum + m.conversions, 0);
                        const rate = totalLeads > 0 ? (totalConversions / totalLeads) * 100 : 0;
                        return rate.toFixed(1);
                      })()}
                      %
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </>
      )}
    </Paper>
  );
};

export default TeamDashboard;
